import React, { useMemo } from 'react';
import { StreamingContainerTreeNodeData } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { backgroundInlineStyles, BACKGROUND_SRC_DEFAULT_CLASSES } from './backgroundInlineStyles';
import { hydrateRichTextVariables, renderRichTextToPlainString, normalizeStreamingRichText } from '../../richtext/ast';
import { transformToContainerQueries } from '../utils/transformBreakpointClasses';
import { cn } from '../../utils/cn';

type DefaultElement = React.ElementType<any, 'div'>;

type PolymorphicContainerProps<T extends React.ElementType = DefaultElement> = {
  id: string;
  as?: T;
  children?: React.ReactNode;
  editorClassName?: string;
  staticAssetsBaseURL?: string | null;
  overrideVariables?: Partial<Record<string, string>>;
  isEditor?: boolean;
} & StreamingContainerTreeNodeData;

type PolymorphicRef<T extends React.ElementType = DefaultElement> = React.ComponentPropsWithRef<T>['ref'];

function ContainerInner<T extends React.ElementType = DefaultElement>(
  {
    as,
    id,
    className,
    editorClassName,
    children,
    backgroundSrc,
    decorative,
    staticAssetsBaseURL,
    overrideVariables,
    href,
    openInNewTab,
    isEditor,
    ...props
  }: PolymorphicContainerProps<T>,
  ref: PolymorphicRef<T>,
) {
  const Element: React.ElementType = href && !isEditor ? 'a' : as || ('div' as DefaultElement);

  const style = useMemo(
    () => backgroundInlineStyles(backgroundSrc, staticAssetsBaseURL),
    [backgroundSrc, staticAssetsBaseURL],
  );

  // Normalize streaming RichText to complete RichText for safe processing
  const normalizedHref = useMemo(() => (href ? normalizeStreamingRichText(href) : undefined), [href]);

  const resolvedHref = useMemo(() => {
    if (!normalizedHref) return normalizedHref;
    let value = normalizedHref;
    if (typeof value !== 'string') {
      value = hydrateRichTextVariables(value, overrideVariables || {}, isEditor);
    }
    if (typeof value !== 'string') {
      value = renderRichTextToPlainString(value);
    }
    return value;
  }, [normalizedHref, overrideVariables, isEditor]);

  const linkProps =
    href && !isEditor
      ? {
          href: resolvedHref,
          target: openInNewTab ? '_blank' : undefined,
          rel: openInNewTab ? 'noopener noreferrer' : undefined,
        }
      : {};

  return (
    <Element
      id={id}
      ref={ref}
      className={cn(
        backgroundSrc ? BACKGROUND_SRC_DEFAULT_CLASSES : undefined,
        transformToContainerQueries(className, { isEditor }),
        editorClassName,
      )}
      {...linkProps}
      {...props}
      style={style}
    >
      {children}
    </Element>
  );
}

ContainerInner.displayName = 'Container';

export const Container = React.forwardRef<PolymorphicRef<React.ElementType>, PolymorphicContainerProps>(
  ContainerInner,
) as typeof ContainerInner;
